/**
 * POC animated lobby — the effects bus.
 *
 * Plain TS outside React (no Zustand slice): features call `play(...)`, the EffectsLayer subscribes
 * and renders whatever is active, each effect calls `finish(id)` when its finite animation ends.
 * The layer unmounts itself when the queue drains, so at rest there is no DOM node, no canvas and
 * `document.getAnimations().length === 0`.
 *
 * `play` is a no-op when the boot flag is off, the motion tier is `off`, or the bus is suspended
 * (the idle manager suspends it while the lobby is hidden behind a game). In `lite` the canvas
 * effects are dropped and the celebrations keep only their DOM part.
 */

import { FX_ENABLED } from './fxFlag';
import { getMotionTier } from './motionTier';

export interface EffectRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface EffectPayloads {
  /** First-load curtain + light sweep. */
  reveal: Record<string, never>;
  /** Light tunnel growing out of the pressed card. */
  gameOpen: { rect: EffectRect };
  /** Big number scaling in with a glow. */
  amountBurst: { amount: string; label: string };
  /** Canvas coin shower (celebrations). */
  coinShower: { seed?: number };
  /** Canvas confetti burst. */
  confetti: { seed?: number };
}

export type EffectName = keyof EffectPayloads;

export type EffectInstance = {
  [N in EffectName]: { id: number; name: N; payload: EffectPayloads[N] };
}[EffectName];

export type EffectsListener = (active: readonly EffectInstance[]) => void;

const CANVAS_EFFECTS: ReadonlySet<EffectName> = new Set<EffectName>(['coinShower', 'confetti']);

export type CanvasEffectInstance = Extract<EffectInstance, { name: 'coinShower' | 'confetti' }>;

export const isCanvasEffect = (effect: EffectInstance): effect is CanvasEffectInstance => CANVAS_EFFECTS.has(effect.name);

let nextId = 1;
let active: readonly EffectInstance[] = [];
let suspended = false;
let layerMounted = false;
const listeners = new Set<EffectsListener>();
const suspendListeners = new Set<(suspended: boolean) => void>();

const emit = (): void => {
  listeners.forEach((listener) => listener(active));
};

const canPlay = (name: EffectName): boolean => {
  if (!FX_ENABLED || suspended) return false;
  const tier = getMotionTier();
  if (tier === 'off') return false;
  if (tier === 'lite' && CANVAS_EFFECTS.has(name)) return false;
  return true;
};

/** Starts one effect. Returns its id, or null when the effect was dropped (flag, tier, idle). */
const play = <N extends EffectName>(name: N, payload: EffectPayloads[N]): number | null => {
  if (!canPlay(name)) return null;

  const id = nextId++;
  // New array every time: the layer compares references.
  active = [...active, { id, name, payload } as EffectInstance];
  emit();
  return id;
};

/** Called by the renderer when an effect's finite animation has ended. */
const finish = (id: number): void => {
  if (!active.some((effect) => effect.id === id)) return;
  active = active.filter((effect) => effect.id !== id);
  emit();
};

/** Drops one effect, or every active effect when called without an id. */
const cancel = (id?: number): void => {
  if (active.length === 0) return;
  active = id === undefined ? [] : active.filter((effect) => effect.id !== id);
  emit();
};

const subscribe = (listener: EffectsListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getActive = (): readonly EffectInstance[] => active;

/** Idle: cancel everything in flight and ignore new plays until `resume`. */
const suspend = (): void => {
  if (suspended) return;
  suspended = true;
  cancel();
  suspendListeners.forEach((listener) => listener(true));
};

const resume = (): void => {
  if (!suspended) return;
  suspended = false;
  suspendListeners.forEach((listener) => listener(false));
};

const isSuspended = (): boolean => suspended;

const subscribeSuspended = (listener: (suspended: boolean) => void): (() => void) => {
  suspendListeners.add(listener);
  return () => {
    suspendListeners.delete(listener);
  };
};

/** The layer reports itself so one-shot boot effects (the reveal) can skip when it is not there yet. */
const setLayerMounted = (mounted: boolean): void => {
  layerMounted = mounted;
};

const isLayerMounted = (): boolean => layerMounted;

export type CelebrationKind = 'jackpotHit' | 'bigWin' | 'confetti';

export interface CelebrationPayload {
  amount?: string;
  label?: string;
  seed?: number;
}

/**
 * The "over the top" composites. `full`: coins/confetti on the canvas plus the DOM amount burst;
 * `lite`: the amount burst only (the bus drops the canvas parts); `off`: nothing.
 */
const playCelebration = (kind: CelebrationKind, payload: CelebrationPayload = {}): void => {
  const seed = payload.seed;
  if (kind === 'confetti') {
    play('confetti', { seed });
    return;
  }

  play('coinShower', { seed });
  if (kind === 'jackpotHit') {
    play('confetti', { seed: seed === undefined ? undefined : seed + 1 });
  }
  if (payload.amount || payload.label) {
    play('amountBurst', { amount: payload.amount ?? '', label: payload.label ?? '' });
  }
};

export const effectsBus = {
  play,
  playCelebration,
  finish,
  cancel,
  subscribe,
  getActive,
  suspend,
  resume,
  isSuspended,
  subscribeSuspended,
  setLayerMounted,
  isLayerMounted,
};
